// src/frontend/src/pages/CreditsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { CreditService } from '../CreditService';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
    Container, Typography, Box, Paper, Button,
    Card, CardContent, Grid, CircularProgress,
    Snackbar, Alert, Divider
} from '@mui/material';
import { 
    AddShoppingCart, WorkspacePremium as PremiumIcon,
    CreditCard, ShoppingCartOutlined,
    LocalAtmOutlined} from '@mui/icons-material';

const CreditsPage = () => {
    const { isAuthenticated, isPremium, login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [credits, setCredits] = useState(0);
    const [creditPackages, setCreditPackages] = useState([]);
    const [purchaseLoading, setPurchaseLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [isLoadingPackages, setIsLoadingPackages] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const fetchCreditBalance = useCallback(async () => {
        try {
            setLoading(true);
            const balance = await CreditService.getCreditBalance();
            setCredits(balance);
        } catch (error) {
            console.error('Error fetching credit balance:', error);
            showSnackbar('Error loading credit balance', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchCreditPackages = useCallback(async () => {
        setIsLoadingPackages(true);
        try {
            const packages = await CreditService.getCreditPackages();
            setCreditPackages(packages);
        } catch (error) {
            console.error('Error fetching credit packages:', error);
            showSnackbar('Error loading credit packages', 'error');
        } finally {
            setIsLoadingPackages(false);
        }
    }, []);

    const processCreditPurchaseSuccess = useCallback(async (sessionId) => {
        try {
            const newCredits = await CreditService.processCreditPurchase(sessionId);
            setCredits(newCredits);
            showSnackbar('Credits purchased successfully!', 'success');
            
            // Clear URL params
            navigate('/credits', { replace: true });
        } catch (error) {
            console.error('Error processing credit purchase:', error);
            showSnackbar('Error processing purchase. Please contact support.', 'error');
        }
    }, [navigate]);

    useEffect(() => {
        // Check if coming back from successful credit purchase
        const params = new URLSearchParams(location.search);
        const sessionId = params.get('session_id');
        
        if (sessionId) {
            processCreditPurchaseSuccess(sessionId);
        }
        
        // Fetch data if authenticated
        if (isAuthenticated) {
            fetchCreditBalance();
            fetchCreditPackages();
        }
    }, [isAuthenticated, location, processCreditPurchaseSuccess, fetchCreditBalance, fetchCreditPackages]);

    const handlePurchaseCredits = async (packageId) => {
        if (!isAuthenticated) {
            login('/subscription'); // Changed from '/credits' to '/subscription'
            return;
        }

        if (!isPremium) {
            navigate('/subscription');
            return;
        }

        setPurchaseLoading(true);
        try {
            const checkoutSession = await CreditService.createCreditCheckoutSession(packageId);
            
            // Redirect to Stripe checkout
            window.location.href = checkoutSession.sessionUrl;
        } catch (error) {
            console.error('Error creating checkout session:', error);
            showSnackbar('Error creating checkout session', 'error');
        } finally {
            setPurchaseLoading(false);
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (!isAuthenticated) {
        return (
            <Container maxWidth="md" sx={{ py: 8 }}>
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h5" gutterBottom>
                        Sign in to View and Manage Credits
                    </Typography>
                    <Typography variant="body1" paragraph>
                        You need to sign in to view your autobuy credits and purchase history.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => login('/subscription')}
                        sx={{ mt: 2 }}
                    >
                        Sign in
                    </Button>
                </Paper>
            </Container>
        );
    }

    if (loading && !credits) {
        return (
            <Container maxWidth="md" sx={{ py: 8 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 8 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Autobuy Credits
            </Typography>
            
            {/* Credit Balance Card */}
            <Paper elevation={3} sx={{ p: 4, mb: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <CreditCard sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                    <Box>
                        <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                            Your Credit Balance
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Use credits to set up automatic reservations
                        </Typography>
                    </Box>
                    <Box sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        px: 3, 
                        py: 1.5, 
                        borderRadius: 2,
                        fontWeight: 'bold',
                        fontSize: '1.5rem',
                        ml: 'auto'
                    }}>
                        {credits} Credit{credits !== 1 ? 's' : ''}
                    </Box>
                </Box>
                
                <Divider sx={{ mb: 3 }} />
                
                <Typography variant="body1" paragraph>
                    Each credit allows you to set up an autobuy for one GPU. When that GPU becomes available, we'll automatically reserve it for you and send you an email notification.
                </Typography>
                
                <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<ShoppingCartOutlined />}
                        component={Link}
                        to="/stocks"
                    >
                        Browse GPUs
                    </Button>
                    
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<LocalAtmOutlined />}
                        component={Link}
                        to="/my-tracked-stocks"
                    >
                        View My Autobuys
                    </Button>
                </Box>
            </Paper>

            {/* Purchase Credits Section */}
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                Purchase Additional Credits
            </Typography>

            {!isPremium ? (
                <Paper elevation={3} sx={{ p: 4, mb: 4, textAlign: 'center' }}>
                    <PremiumIcon sx={{ fontSize: 56, color: 'gold', mb: 2 }} />
                    <Typography variant="h5" gutterBottom>
                        Premium Membership Required
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Autobuy credits are available exclusively to premium members.
                        Subscribe to unlock access to credits and receive 1 free credit each month!
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        component={Link}
                        to="/subscription"
                        sx={{ mt: 2 }}
                        startIcon={<PremiumIcon />}
                    >
                        Upgrade to Premium
                    </Button>
                </Paper>
            ) : (
                <Grid container spacing={3} sx={{ mb: 6 }}>
                    {isLoadingPackages ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {creditPackages.map((pkg, index) => (
                                <Grid item xs={12} sm={4} key={index}>
                                    <Card sx={{ 
                                        height: '100%',
                                        transition: 'transform 0.2s',
                                        border: index === 1 ? '2px solid #5865F2' : 'none',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: 4
                                        }
                                    }}>
                                        {index === 1 && (
                                            <Box sx={{ bgcolor: '#5865F2', color: 'white', py: 1, textAlign: 'center' }}>
                                                <Typography variant="subtitle2">BEST VALUE</Typography>
                                            </Box>
                                        )}
                                        <CardContent sx={{ textAlign: 'center' }}>
                                            <Typography variant="h5" component="div" gutterBottom>
                                                {pkg.amount} Credit{pkg.amount !== 1 ? 's' : ''}
                                            </Typography>
                                            <Typography variant="h4" color="primary" gutterBottom>
                                                ${pkg.price.toFixed(2)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                {pkg.description}
                                            </Typography>
                                            <Button 
                                                variant="contained" 
                                                fullWidth
                                                startIcon={<AddShoppingCart />}
                                                onClick={() => handlePurchaseCredits(index)}
                                                disabled={purchaseLoading}
                                            >
                                                {purchaseLoading ? <CircularProgress size={24} /> : 'Purchase'}
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </>
                    )}
                </Grid>
            )}
            
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default CreditsPage;